/* eslint-disable max-lines */
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '@material-ui/core';
import { getContributorAvatarUrl } from 'common/constants/Avatars';
import MultipleAvatars from 'components/MultipleAvatars';
import Text from 'components/base/Text';
import Chip from 'components/base/Chip';
import Icon from 'components/base/Icon';
import { Flex } from 'components/base/layout-components';
import { HyperLink } from 'components/base/HyperLink';
import {
  getLastActivityPhrase,
  columnSummaryMapper,
  nullSafeGet
} from 'common/utils/utils';
import { getDefaultFormatter } from 'common/utils/time.utils';
import { getDurationLevels, DATE_FORMAT } from '@linearb/linui-utils';

import { ICON_SIZES, SPACES } from 'common/constants/sizes';
import { EMPTY_NUMBER, prStateToIconMap } from 'common/constants/constants';
import numeral from 'numeral';
import { getPrTotalSize } from 'common/utils/prs.utils';

export const PR_STATES = {
  HAS_NO_PR: { key: 'HAS_NO_PR', filter: 'has-no-pr' },
  INITIATED: { key: 'INITIATED', filter: 'initiated' },
  IN_REVIEW: { key: 'IN_REVIEW', filter: 'in-review' },
  MERGED: { key: 'MERGED', filter: 'merged' },
  DRAFT: { key: 'DRAFT', filter: 'draft' }
};

const prStateToChipTypeMap = {
  initiated: 'primary',
  'in-review': 'warning',
  closed: 'error',
  merged: 'success',
  draft: 'draft'
};

const ClickableCell = styled.div`
  cursor: pointer;
`;

export const getColumns = (prs, prMetricsPerBranchContributorsEnabled) =>
  [
    {
      dataPoint: 'title',
      name: 'PULL REQUESTS',
      isSortedCol: true,
      summary: 'total',
      tableCellProps: {
        style: {
          borderRight: 0,
          minWidth: '10rem'
        }
      }
    },
    {
      dataPoint: 'state',
      name: 'STATE',
      isSortedCol: true,
      tableCellProps: {
        style: {
          borderLeft: 0
        }
      }
    },
    prMetricsPerBranchContributorsEnabled
      ? {
          dataPoint: 'issuer_name',
          name: 'CONTRIBUTORS',
          isSortedCol: false
        }
      : {
          dataPoint: 'issuer_name',
          name: 'AUTHORS',
          isSortedCol: true,
          summary: 'summary.authors'
        },
    {
      dataPoint: 'repository_name',
      name: 'REPOSITORIES',
      isSortedCol: true,
      summary: 'summary.repositories',
      tableCellProps: {
        style: {
          minWidth: '5rem'
        }
      }
    },
    {
      dataPoint: 'created_at',
      name: 'CREATED',
      isSortedCol: true,
      tableCellProps: {
        style: {
          borderRight: 0
        }
      }
    },
    {
      dataPoint: 'updated_at',
      name: 'UPDATED',
      isSortedCol: true,
      tableCellProps: {
        style: {
          borderLeft: 0
        }
      }
    },
    {
      dataPoint: 'comment_count',
      name: 'COMMENTS',
      isSortedCol: true,
      summary: 'summary.comment_count',
      tableCellProps: {
        style: {
          borderRight: 0
        }
      }
    },
    {
      dataPoint: 'unreviewed',
      name: 'REVIEWED',
      isSortedCol: false,
      tableCellProps: {
        style: {
          borderRight: 0,
          borderLeft: 0
        }
      }
    },
    {
      dataPoint: 'review_time',
      name: 'REVIEW TIME',
      isSortedCol: true,
      tableCellProps: {
        style: {
          borderLeft: 0
        }
      }
    },
    {
      dataPoint: 'pr.size.total',
      name: 'PR SIZE',
      isSortedCol: true,
      tableCellProps: {
        style: {
          borderLeft: 1
        }
      }
    },
    {
      dataPoint: 'labels',
      name: 'LABELS',
      isSortedCol: false,
      tableCellProps: {
        style: {
          borderLeft: 1
        }
      }
    }
  ].map(column => columnSummaryMapper(column, prs, 'middle'));

export const filterChipLabelMap = {
  authors: 'Author',
  contributors: 'Contributor',
  repositories: 'Repository',
  filtered_teams: 'Team',
  services: 'Service',
  labels: 'PR Label'
};

export const getPrsTableData = ({
  contributorsById,
  data,
  contributors,
  onContributorClick = () => null,
  onRepositoryClick = () => null,
  myAccount,
  showPersonalInformation,
  prMetricsPerBranchContributorsEnabled
}) => {
  return data.map(pr => {
    const issuer_id = _.get(pr, 'issuer_id', null);
    const effectiveContributorIds = _.get(pr, 'effective_contributor_ids', []);

    const contributors = (
      prMetricsPerBranchContributorsEnabled
        ? effectiveContributorIds
        : [issuer_id]
    ).map(id => {
      const contributor = contributorsById[id];
      const email = _.get(contributor, 'email', '');
      const name = _.get(contributor, 'name', 'N/A');
      const author = {
        url: getContributorAvatarUrl(contributor),
        name,
        email: email || '',
        id
      };
      return author;
    });
    const isDraft = _.get(pr, 'is_draft', false);
    const prName = _.get(pr, 'title', '');
    const prUrl = _.get(pr, 'html_url', null);
    const state = isDraft ? 'draft' : _.get(pr, 'state', '-');
    const repositoryName = _.get(pr, 'repository_name', null);
    const unreviewed = _.get(pr, 'unreviewed', null);
    const isSelfReviewed = _.get(pr, 'is_self_reviewed');
    const lastActivity = _.get(pr, 'last_activity', '-');
    const lastActivityType =
      _.get(pr, 'last_activity.type', null) &&
      prStateToIconMap[lastActivity.type];
    const lastActivityProviderId = _.get(
      contributorsById[_.get(lastActivity, 'author_id', null)],
      'extended_data.provider_id',
      null
    );
    const labels = _.get(pr, 'labels', []);
    const accountProviderId = nullSafeGet(
      myAccount,
      'settings.provider_id',
      EMPTY_NUMBER
    ).toString();
    const reviewTime =
      pr.review_time !== null && !isSelfReviewed
        ? getDurationLevels({
            minutes: pr.review_time,
            format: getDefaultFormatter({ minutes: pr.review_time })
          })
        : '-';

    const getPRSize = () => {
      const size = getPrTotalSize(pr);
      if (isNaN(size)) {
        return '-';
      }
      return numeral(size).format(size > 1000 ? '0.0a' : '0');
    };

    const getCommentCount = () => {
      if (isSelfReviewed) {
        return 'Self review';
      }
      if (
        _.isNumber(pr.team_comment_count) &&
        pr.comment_count !== pr.team_comment_count
      ) {
        return `${pr.comment_count} (${
          pr.comment_count - pr.team_comment_count
        } external)`;
      }
      return pr.comment_count;
    };

    const getPRLabelsTooltip = () =>
      labels.map(({ name, color }) => (
        <Chip
          key={name}
          label={name}
          size='small'
          variant='outlined'
          color={color}
          isFitContent={true}
        />
      ));
    const getPRLabelsCount = () => labels.length;
    return {
      linkUrl: prUrl,
      tooltipText: 'View pull request',
      issuer_name: (
        <MultipleAvatars
          avatars={contributors}
          onClick={showPersonalInformation && onContributorClick}
          size={ICON_SIZES['x-medium']}
        />
      ),
      title: (
        <>
          <Tooltip
            title={
              <Flex items='center' padding='x-x-small'>
                <Icon name='Branch' size='smaller' />
                <Text size='medium' pl='0.5rem'>
                  {prName}
                </Text>
              </Flex>
            }
          >
            {prUrl ? (
              <HyperLink
                target='_blank'
                rel='noopener noreferrer'
                href={prUrl}
                multiLineEllipsisAfter={2}
              >
                <Text size='large' cursor='pointer' color='chambray'>
                  {prName}
                </Text>
              </HyperLink>
            ) : (
              <Text size='large' multiLineEllipsisAfter={2}>
                {prName}
              </Text>
            )}
          </Tooltip>
          {lastActivity ? (
            <Flex items='center' pt='x-small'>
              {lastActivityType && (
                <Icon
                  name={lastActivityType.name}
                  size='x-small'
                  color={lastActivityType.color}
                />
              )}
              <Text color='gray' pl={SPACES['x-small']}>
                {getLastActivityPhrase(
                  accountProviderId,
                  lastActivityProviderId,
                  lastActivity,
                  'author_id',
                  contributorsById
                )}
              </Text>
            </Flex>
          ) : null}
        </>
      ),
      state:
        state !== '-' ? (
          <Flex items='center'>
            <Chip
              label={state}
              size='small'
              variant='outlined'
              color={prStateToChipTypeMap[state]}
              isDashed={isDraft}
            />
          </Flex>
        ) : (
          <Text size='large' align='center'>
            -
          </Text>
        ),
      repository_name: (
        <ClickableCell onClick={() => onRepositoryClick(repositoryName)}>
          <Tooltip title={repositoryName}>
            <Text size='large' color='chambray' multiLineEllipsisAfter={2}>
              {repositoryName}
            </Text>
          </Tooltip>
        </ClickableCell>
      ),
      created_at: (
        <Text size='large'>{moment(pr.created_at).format(DATE_FORMAT)}</Text>
      ),
      updated_at: <Text size='large'>{moment(pr.updated_at).from()}</Text>,
      comment_count: <Text size='large'>{getCommentCount()}</Text>,
      unreviewed: (
        <Flex items='center' justify='flex-start' content='center' pl='small'>
          {_.isBoolean(unreviewed) ? (
            <Icon
              name={
                unreviewed || isSelfReviewed
                  ? 'HighlightOff'
                  : 'CheckCircleOutlineIcon'
              }
              color={unreviewed ? 'torchRed' : 'malachite'}
            />
          ) : (
            <Text size='large' pl='0.6rem'>
              -
            </Text>
          )}
        </Flex>
      ),
      review_time: <Text size='large'>{reviewTime}</Text>,
      'pr.size.total': <Text size='large'>{getPRSize()}</Text>,
      labels: (
        <>
          <Tooltip
            disableHoverListener={labels.length === 0}
            title={
              <Flex
                items='center'
                pt='x-small'
                pb='x-small'
                gap='0.5rem'
                wrap='wrap'
                maxWidth='20rem'
              >
                {getPRLabelsTooltip()}
              </Flex>
            }
          >
            <Text size='large'>{getPRLabelsCount()}</Text>
          </Tooltip>
        </>
      )
    };
  });
};
